import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import SEO from '../components/SEO';
import { getPillarBySlug, matchesPillar } from '../data/pillarsData';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PillarPage = () => {
  const { pillarSlug } = useParams();
  const pillar = getPillarBySlug(pillarSlug);
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    setPosts(null);
    api('/api/blog')
      .then((all) => {
        if (!Array.isArray(all) || !pillar) { setPosts([]); return; }
        const filtered = all
          .filter((p) => matchesPillar(p, pillar))
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setPosts(filtered);
      })
      .catch(() => setPosts([]));
  }, [pillarSlug, pillar]);

  if (!pillar) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-stone-900 mb-3">Topic not found</h1>
          <p className="text-stone-500 mb-6">We couldn&rsquo;t find that pillar.</p>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-rose-500 hover:text-rose-600 font-medium"
            data-testid="pillar-not-found-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  const path = `/articles/pillar/${pillar.slug}`;
  const canonical = `https://akirasignaturecare.com${path}`;
  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${canonical}#collection`,
    url: canonical,
    name: pillar.headline,
    description: pillar.metaDescription,
    isPartOf: { '@id': 'https://akirasignaturecare.com/#website' },
    about: { '@id': 'https://akirasignaturecare.com/#clinic' },
    inLanguage: 'en-US',
    ...(posts && posts.length > 0
      ? {
          hasPart: posts.map((p) => ({
            '@type': 'BlogPosting',
            headline: p.title,
            url: `https://akirasignaturecare.com/articles/${p.slug}`,
            datePublished: p.created_at,
            dateModified: p.updated_at || p.created_at,
            image: p.cover_image,
          })),
          dateModified: posts[0].updated_at || posts[0].created_at,
        }
      : {}),
  };
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://akirasignaturecare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Articles', item: 'https://akirasignaturecare.com/articles' },
      { '@type': 'ListItem', position: 3, name: pillar.headline, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid={`pillar-page-${pillar.slug}`}>
      <SEO
        title={pillar.metaTitle}
        description={pillar.metaDescription}
        path={path}
        schema={[collectionSchema, breadcrumb]}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-rose-900 text-white py-24">
        <div className="container mx-auto px-6">
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors text-sm"
            data-testid="pillar-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> All Articles
          </Link>
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-rose-300 text-sm mb-4">
              <Sparkles className="w-4 h-4" />
              <span className="tracking-[0.3em] uppercase text-xs">Topic Pillar</span>
            </div>
            <h1
              className="font-serif text-4xl lg:text-5xl mb-5 leading-tight"
              data-testid="pillar-headline"
              style={{ textShadow: '0 2px 24px rgba(0,0,0,0.45)' }}
            >
              {pillar.headline}
            </h1>
            <p className="text-lg text-stone-300 leading-relaxed font-light">
              {pillar.description}
            </p>
          </div>
        </div>
      </section>

      {/* List */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          {posts === null && (
            <p className="text-stone-400 text-center">Loading articles&hellip;</p>
          )}

          {posts && posts.length === 0 && (
            <div className="max-w-2xl mx-auto text-center bg-white border border-stone-100 shadow-sm p-12">
              <h2 className="font-serif text-2xl text-stone-800 mb-3">
                No articles in this topic yet.
              </h2>
              <p className="text-stone-500 font-light mb-6">
                Our physicians are working on the launch library &mdash; check back soon.
              </p>
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 text-rose-500 hover:text-rose-600 font-medium"
                data-testid="pillar-empty-back"
              >
                Browse all articles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {posts && posts.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/articles/${p.slug}`}
                  data-testid={`pillar-article-${p.slug}`}
                  className="group block bg-white border border-stone-200 hover:border-rose-300 hover:-translate-y-1.5 hover:shadow-xl transition-all duration-500 ease-out shadow-sm overflow-hidden"
                >
                  {p.cover_image ? (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-stone-100 via-rose-50 to-stone-50 flex items-center justify-center">
                      <Sparkles className="w-10 h-10 text-rose-300" />
                    </div>
                  )}
                  <div className="p-6">
                    {p.created_at && (
                      <time
                        dateTime={new Date(p.created_at).toISOString()}
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-stone-500 mb-3"
                      >
                        <Calendar className="w-3 h-3" />
                        {formatDate(p.created_at)}
                      </time>
                    )}
                    <h2
                      className="font-serif text-xl text-stone-900 mb-3 leading-snug group-hover:text-rose-500 transition-colors"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {p.title}
                    </h2>
                    <p className="text-stone-600 text-sm leading-relaxed line-clamp-3 mb-4">
                      {p.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-stone-100 text-xs">
                      <div className="text-stone-500">
                        <div className="font-medium text-stone-800">{p.author_name}</div>
                        {p.reading_time_min ? (
                          <div className="flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            {p.reading_time_min} min read
                          </div>
                        ) : null}
                      </div>
                      <ArrowRight className="w-4 h-4 text-rose-500 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PillarPage;
